/**
 * list-action-definitions — kontrakty WŁASNYCH akcji tenanta (Akcje list, Faza 2).
 *
 * Model dwuwarstwowy (jak w Fazie 1, patrz `list-actions.ts`):
 *  - KATALOG WBUDOWANY (`list-actions.ts`, w kodzie) = akcje, które umie każda kartoteka.
 *  - KATALOG WŁASNY (ten plik + tabela `list_action_definitions` per tenant) = akcje, które
 *    tenant/ITE definiuje sam.
 *
 * Dwa typy akcji (decyzje właścicielki 2026-09-10):
 *  - `operation` — gotowa operacja bez kodu: ustaw pole / zmień kategorię / dodaj-usuń grupa,
 *  - `sql`       — autorski SQL (wariant pro), wykonywany bezpiecznym sandboxem (ADR-0101).
 * Mail / webhook / powiązanie — świadomie POZA zakresem.
 *
 * Definicja własna wpina się w resolver/paletę Fazy 1 jako akcja `custom:<uuid>` z jednolitym
 * `dispatch` (POST na run-endpoint). Ten plik to SAME kształty + Zod (współdzielone przez
 * walidację `apps/api` i edytor `apps/admin`) — ZERO logiki wykonania.
 */

import { z } from 'zod';

import { type ListActionKartotekaKey } from './list-actions';

// ── Typ akcji (kolumna `action_type`) ─────────────────────────────────────────

export const LIST_ACTION_DEFINITION_TYPES = ['operation', 'sql'] as const;
export type ListActionDefinitionType = (typeof LIST_ACTION_DEFINITION_TYPES)[number];

export function isListActionDefinitionType(v: string): v is ListActionDefinitionType {
  return (LIST_ACTION_DEFINITION_TYPES as readonly string[]).includes(v);
}

/** Prefiks `actionId` dla akcji własnych w konfiguracji layoutu / resolverze (T25F2.6). */
export const CUSTOM_ACTION_ID_PREFIX = 'custom:' as const;

// ── Rodzaje operacji (bez kodu) ───────────────────────────────────────────────

export const OPERATION_KINDS = ['set_field', 'set_category', 'add_group', 'remove_group'] as const;
export type OperationKind = (typeof OPERATION_KINDS)[number];

/**
 * `mode` akcji SQL: `per_record` woła SQL raz na każdy zaznaczony rekord (kontekst `:record_id`);
 * `bulk_set` woła raz z całym zbiorem `:selected_ids`.
 */
export const LIST_ACTION_SQL_MODES = ['per_record', 'bulk_set'] as const;
export type ListActionSqlMode = (typeof LIST_ACTION_SQL_MODES)[number];

// ── type_config (dyskryminowana unia po `kind`) ───────────────────────────────

/** „Ustaw pole": nadpisuje jedno pole kartoteki wartością na zaznaczonych rekordach. */
export const setFieldConfigSchema = z.object({
  kind: z.literal('set_field'),
  /** Slug pola kartoteki (walidowany serwerowo względem katalogu pól encji). */
  field: z.string().trim().min(1).max(63),
  /** Nowa wartość (string; serwer rzutuje wg typu pola). Pusta = wyczyszczenie pola. */
  value: z.string(),
});

/** „Zmień kategorię": ustawia oś kategorii (`record_categories`) rekordu. */
export const setCategoryConfigSchema = z.object({
  kind: z.literal('set_category'),
  categoryId: z.string().guid(),
});

/** „Dodaj do grupy" / „Usuń z grupy": modyfikuje zbiór grup rekordu (nie nadpisuje). */
export const addGroupConfigSchema = z.object({
  kind: z.literal('add_group'),
  groupId: z.string().trim().min(1).max(100),
});
export const removeGroupConfigSchema = z.object({
  kind: z.literal('remove_group'),
  groupId: z.string().trim().min(1).max(100),
});

/** „SQL": autorskie zapytanie wykonywane bezpiecznym sandboxem (ADR-0101). */
export const sqlConfigSchema = z.object({
  kind: z.literal('sql'),
  sql: z.string().trim().min(1),
  mode: z.enum(LIST_ACTION_SQL_MODES).default('bulk_set'),
});

export const listActionTypeConfigSchema = z.discriminatedUnion('kind', [
  setFieldConfigSchema,
  setCategoryConfigSchema,
  addGroupConfigSchema,
  removeGroupConfigSchema,
  sqlConfigSchema,
]);
export type ListActionTypeConfig = z.infer<typeof listActionTypeConfigSchema>;

/** `action_type` wyprowadzany z `kind` configu: `sql` → 'sql', reszta → 'operation'. */
export function actionTypeForConfig(config: ListActionTypeConfig): ListActionDefinitionType {
  return config.kind === 'sql' ? 'sql' : 'operation';
}

// ── „Zmień zbiorczo" — akcja jednorazowa (PHASE 25 Faza 2 §8) ─────────────────

/**
 * Operacja wsadowa = ta sama gotowa operacja bez kodu co w definicji własnej, ale BEZ typu
 * `sql` (odchudzony modal „Zmień zbiorczo" nie oferuje autorskiego SQL). Kilka operacji leci
 * naraz na zaznaczonych rekordach w JEDNEJ transakcji (porażka którejkolwiek → rollback całości,
 * decyzja właścicielki 2026-09-10).
 */
export const bulkChangeOperationSchema = z.discriminatedUnion('kind', [
  setFieldConfigSchema,
  setCategoryConfigSchema,
  addGroupConfigSchema,
  removeGroupConfigSchema,
]);
export type BulkChangeOperation = z.infer<typeof bulkChangeOperationSchema>;

/** Body `POST /api/list-actions/bulk-change` — zbiór zmian + zaznaczone rekordy kartoteki. */
export const bulkChangeBodySchema = z.object({
  /** Klucz kartoteki listy (walidacja zakresu jak w run-endpoincie akcji własnych). */
  kartotekaKey: z.string().trim().min(1),
  /** Slug kartoteki systemowej → fizyczna tabela encji (np. `sale_invoices`, `contractors`). */
  slug: z.string().trim().min(1).max(63),
  /** Zaznaczone rekordy. */
  selectedIds: z.array(z.string().guid()).min(1),
  /** Lista zmian do wykonania po kolei w jednej transakcji (min. 1). */
  operations: z.array(bulkChangeOperationSchema).min(1).max(20),
});
export type BulkChangeBody = z.infer<typeof bulkChangeBodySchema>;

// ── Zakres (które kartoteki) — mirror `ListActionScope` z Fazy 1 ─────────────

/**
 * `applies_to`: `'all'` = każda kartoteka; lista kluczy = tylko wskazane. Przechowywane jako
 * jsonb (sentinel `'all'` albo tablica kluczy) — kształt spójny z `ListActionScope`.
 */
export const listActionAppliesToSchema = z.union([
  z.literal('all'),
  z.array(z.custom<ListActionKartotekaKey>()).min(1),
]);
export type ListActionAppliesTo = z.infer<typeof listActionAppliesToSchema>;

// ── DTO wiersza katalogu ─────────────────────────────────────────────────────

export interface ListActionDefinitionDTO {
  id: string;
  /** Etykieta w menu — wolny tekst tenanta (BEZ klucza i18n, inaczej niż akcje wbudowane). */
  name: string;
  description: string | null;
  icon: string | null;
  actionType: ListActionDefinitionType;
  typeConfig: ListActionTypeConfig;
  appliesTo: ListActionAppliesTo;
  /** Zawężenie do klas dokumentu (tylko gdy `appliesTo` obejmuje `documents`). */
  documentClasses: string[] | null;
  /** Wymagaj potwierdzenia przed wykonaniem (czerwony wariant + dialog). */
  requireConfirm: boolean;
  /** Role tenanta, które mogą WYKONAĆ akcję. Pusta = wszystkie (bramka rolą do czasu uprawnień). */
  allowedRoleIds: string[];
  /** Bramka modułu (billing) wymagana do dostępności (nullable). */
  moduleCode: string | null;
  isActive: boolean;
}

// ── Body request (create/patch) — współdzielone przez API i edytor ───────────

export const listActionDefinitionCreateSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  icon: z.string().trim().max(100).optional(),
  typeConfig: listActionTypeConfigSchema,
  appliesTo: listActionAppliesToSchema,
  documentClasses: z.array(z.string().trim().min(1)).nonempty().optional(),
  requireConfirm: z.boolean().default(false),
  allowedRoleIds: z.array(z.string().guid()).default([]),
  moduleCode: z.string().trim().min(1).max(100).nullable().optional(),
  isActive: z.boolean().default(true),
});
export type ListActionDefinitionCreate = z.infer<typeof listActionDefinitionCreateSchema>;

export const listActionDefinitionPatchSchema = listActionDefinitionCreateSchema
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Podaj co najmniej jedno pole' });
export type ListActionDefinitionPatch = z.infer<typeof listActionDefinitionPatchSchema>;
